
import { PublicKey } from '@solana/web3.js';

export type AddressType = 'bsc' | 'solana' | 'unknown';

export interface AddressValidationResult {
  isValid: boolean;
  type: AddressType;
  normalizedAddress: string;
}

/**
 * Validates and determines the type of a wallet address
 */
export const validateAddress = (address: string): AddressValidationResult => {
  if (!address) {
    return { isValid: false, type: 'unknown', normalizedAddress: '' };
  }

  const trimmedAddress = address.trim();

  // Check if it's a BSC/Ethereum address (0x... format)
  if (trimmedAddress.match(/^0x[a-fA-F0-9]{40}$/)) {
    return {
      isValid: true,
      type: 'bsc',
      normalizedAddress: trimmedAddress.toLowerCase()
    };
  }

  // Check if it's a Solana address
  try {
    const publicKey = new PublicKey(trimmedAddress);
    // Additional validation - Solana addresses are typically 32-44 characters
    if (trimmedAddress.length >= 32 && trimmedAddress.length <= 44) {
      return {
        isValid: true,
        type: 'solana',
        normalizedAddress: publicKey.toString()
      };
    }
  } catch (error) {
    // Not a valid Solana address
  }

  return { isValid: false, type: 'unknown', normalizedAddress: trimmedAddress };
};

/**
 * Formats an address for display (shortens it)
 */
export const formatAddress = (address: string, type?: AddressType): string => {
  if (!address) return '';
  
  const validation = type ? { type } : validateAddress(address);
  
  if (validation.type === 'bsc') {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  } else if (validation.type === 'solana') {
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  }
  
  return address;
};

/**
 * Checks if two addresses are the same (case-insensitive for BSC)
 */
export const addressesEqual = (addr1: string, addr2: string): boolean => {
  if (!addr1 || !addr2) return false;
  
  const validation1 = validateAddress(addr1);
  const validation2 = validateAddress(addr2);
  
  if (validation1.type !== validation2.type) return false;
  
  return validation1.normalizedAddress === validation2.normalizedAddress;
};
